// OWNER: AREA-EVENTS (event-types/*, messages/*, requests/*). Components go in src/features/events.
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Mail, RotateCw, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import type { PagedPagination } from '@/api/pagination'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { PageHeader } from '@/components/PageHeader'
import { Button, IconButton } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Tooltip } from '@/components/ui/tooltip'
import { useMessagesPage } from '@/features/events/api'
import {
  hasActiveFilters,
  parseMessageSearch,
  type MessageSearch,
} from '@/features/events/message-filters'
import { messageColumns } from '@/features/events/messages/columns'
import { MessageFilters } from '@/features/events/messages/MessageFilters'
import { readStorage, writeStorage } from '@/lib/storage'
import { useAppScope } from '@/shell/useAppScope'

export const Route = createFileRoute('/messages/')({
  validateSearch: (s: Record<string, unknown>): MessageSearch => parseMessageSearch(s),
  component: MessagesPage,
})

const LIVE_KEY = 'messages.live'
const LIVE_INTERVAL = 10_000

function MessagesPage() {
  const navigate = useNavigate({ from: Route.fullPath })
  const search = Route.useSearch()
  const { appId, application } = useAppScope()
  const [live, setLive] = useState(() => readStorage(LIVE_KEY) === 'true')
  const columns = useMemo(() => messageColumns({ showApp: !appId }), [appId])
  const filters = useMemo(() => ({ ...search, app: appId }), [search, appId])
  const page = useMessagesPage(filters, { refetchInterval: live ? LIVE_INTERVAL : false })
  const filtered = hasActiveFilters(search)

  const setFilters = (patch: Partial<MessageSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  return (
    <>
      <PageHeader
        title="Messages"
        description={
          application
            ? `Every email ${application.name} sent through MailSmith, with its delivery status.`
            : 'Every email MailSmith accepted, with its delivery status. Search by recipient to answer “did they get it?”'
        }
        actions={
          <>
            <Tooltip
              content={
                live ? 'Refreshing every 10 seconds' : 'Refresh automatically every 10 seconds'
              }
            >
              <label className="flex h-8 cursor-pointer items-center gap-2 rounded-md px-2 text-sm text-ink-2 hover:bg-surface-3">
                <Switch
                  checked={live}
                  onCheckedChange={(v) => {
                    setLive(v)
                    writeStorage(LIVE_KEY, String(v))
                  }}
                  aria-label="Auto-refresh"
                />
                Auto-refresh
                {live && (
                  <span className="relative flex size-1.5" aria-hidden>
                    <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-60" />
                    <span className="relative inline-flex size-1.5 rounded-full bg-success" />
                  </span>
                )}
              </label>
            </Tooltip>
            <IconButton
              label="Refresh"
              variant="secondary"
              onClick={() => void page.refetch()}
              disabled={page.isFetching}
            >
              <RotateCw className={page.isFetching ? 'animate-spin' : undefined} />
            </IconButton>
          </>
        }
      />
      <DataTable
        aria-label="Messages"
        density="compact"
        columns={columns}
        data={page.items}
        getRowId={(m) => m.id}
        isLoading={page.isPending}
        error={page.error}
        onRetry={() => void page.refetch()}
        pagination={withScrollToTop(page.pagination)}
        persistKey="messages"
        // Subject already says which email it was; the template key is one
        // click away in Columns and on the detail page.
        initialColumnVisibility={{ template_key: false }}
        onRowClick={(m, e) => {
          if ('metaKey' in e && (e.metaKey || e.ctrlKey)) {
            window.open(`/messages/${m.id}${appId ? `?app=${appId}` : ''}`, '_blank', 'noopener')
            return
          }
          void navigate({ to: '/messages/$messageId', params: { messageId: m.id } })
        }}
        toolbar={<MessageFilters value={search} onChange={setFilters} appId={appId} />}
        empty={
          filtered ? (
            <EmptyState
              compact
              icon={Search}
              title="No messages match these filters"
              description={
                search.q
                  ? `Nothing sent to or with ID “${search.q}” in this range. Recipient search matches the full address, or the start of one.`
                  : 'Try a wider time range or fewer filters.'
              }
              action={
                <Button
                  size="sm"
                  onClick={() =>
                    setFilters({
                      status: undefined,
                      event: undefined,
                      q: undefined,
                      range: undefined,
                      since: undefined,
                      until: undefined,
                      test: undefined,
                    })
                  }
                >
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={Mail}
              title="No messages yet"
              description="Messages appear here when an application posts an event that matches a rule, or when someone sends a test. Open an event type and use Simulate to get a ready-to-run curl command."
              action={
                <Button asChild>
                  <Link to="/event-types">Go to event types</Link>
                </Button>
              }
            />
          )
        }
      />
    </>
  )
}

/** Page changes start at the top of the new page, not the old scroll offset. */
function withScrollToTop(p: PagedPagination): PagedPagination {
  const top = () => document.getElementById('main')?.scrollTo({ top: 0 })
  return {
    ...p,
    next: () => {
      p.next()
      top()
    },
    prev: () => {
      p.prev()
      top()
    },
  }
}
